import { KittlHeaderNames } from '@repo/api-client';
import { User } from '@repo/domain/user';
import { USER_API_URL } from '@repo/editor/src/api/constants';
import { useUserStore } from '@repo/editor/src/stores/userStore';
import { PoWChallenge } from '@repo/editor/src/types';
import {
  fetchRetry,
  retryOnStatusCode,
  tryFetchRetry,
} from '@repo/editor/src/utils/fetchRetry';
import { HeaderNames } from '@repo/transport/fetchRetry';

import { useWorkspaceStore } from 'stores/workspaceStore';

type GenericResponse<T extends object> =
  | { success?: false; error: string }
  | ({ success: true } & T);

type GenericSearchArgs<T = unknown> = T & {
  take: number;
  skip: number;
  query: string;
  sort: string;
  trash?: boolean;
};

interface UserProfile {
  profileImage: File;
  name: string;
  marketingOptIn: string;
  description: string;
  location: string;
  slug: string;
  email: string;
  instagram: string;
  website: string;
  shippingInfo: string;
  allowCommentEmails: string;
  allowShareViewEmails: string;
}
/**
 * get the current accessToken
 */
export const getAccessToken = useUserStore.getState().getProfileToken;

const createAPI = ({ url }: { url: string }) => {
  const resetPassword = async (recoveryCode: string, newPassword: string) => {
    const resp = await fetchRetry(`${url}/users/reset_password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ recoveryCode, newPassword }),
    });

    return await resp.json();
  };

  // update the user
  const update = async ({
    profileImage,
    name,
    marketingOptIn,
    description,
    location,
    slug,
    email,
    instagram,
    website,
    shippingInfo,
    allowCommentEmails,
    allowShareViewEmails,
  }: Partial<UserProfile>) => {
    const data = new FormData();
    profileImage && data.append('profileImage', profileImage);
    name && data.append('name', name);
    marketingOptIn !== undefined &&
      data.append('marketingOptIn', marketingOptIn);
    (description || description === '') &&
      data.append('description', description);
    (location || location === '') && data.append('location', location);
    slug && data.append('slug', slug);
    email && data.append('email', email);
    (instagram || instagram === '') && data.append('instagram', instagram);
    (website || website === '') && data.append('website', website);
    shippingInfo && data.append('shippingInfo', shippingInfo);
    allowCommentEmails !== undefined &&
      data.append('allowCommentEmails', allowCommentEmails);
    allowShareViewEmails !== undefined &&
      data.append('allowShareViewEmails', allowShareViewEmails);

    const resp = await fetch(`${url}/users/update`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: data,
    });
    const result = await resp.json();

    return result;
  };

  const confirmEmail = async (code: string | string[]) => {
    const resp = await fetchRetry(`${url}/users/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`code=${code}`),
    });

    const result = await resp.json();
    return result;
  };

  const requestConfirmationCode = async () => {
    const resp = await fetchRetry(`${url}/users/request_confirmation_code`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });

    const result = await resp.json();
    return result;
  };

  const getShare = async (id: string) => {
    const resp = await fetchRetry(`${url}/share/${id}`, {
      method: 'GET',
    });
    const result = await resp.json();

    return result;
  };

  const sendFeedback = async (
    email: string,
    message: string,
    name: string,
    subject: string,
    powChallenge: PoWChallenge
  ) => {
    const resp = await fetchRetry(`${url}/users/send_feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-challenge-nonce': powChallenge.nonce ?? '',
        'x-challenge-solution': powChallenge.solution?.toString() ?? '',
      },
      body: JSON.stringify({
        email,
        message,
        name,
        subject,
      }),
    });
    const result = await resp.json();

    return result;
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    const resp = await fetchRetry(`${url}/users/change_password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const result = await resp.json();

    return { ...result, status: resp.status };
  };

  /**
   * Invitations
   *
   */
  const getInvites = async () => {
    const resp = await fetchRetry(`${url}/invites/index`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    const result = await resp.json();

    return result;
  };

  const createInvite = async (email: string) => {
    const resp = await fetchRetry(`${url}/invites/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ email }),
    });
    const result = await resp.json();

    return result;
  };

  const getPublicUser = async (slug: string) => {
    const resp = await fetchRetry(`${url}/users/public/${slug}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    const result = await resp.json();

    return result;
  };

  const getPromoCode = async (name: string) => {
    const resp = await fetchRetry(`${url}/promocodes/${name}`, {
      method: 'GET',
    });

    const result = await resp.json();

    return result;
  };

  const follow = async (slug: string) => {
    const resp = await fetchRetry(`${url}/follows/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: encodeURI(`user=${slug}`),
    });
    const result = await resp.json();

    return result;
  };

  const unfollow = async (slug: string) => {
    const resp = await fetchRetry(`${url}/follows/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: encodeURI(`user=${slug}`),
    });
    const result = await resp.json();

    return result;
  };

  const viewShare = async (shareId: string) => {
    const resp = await fetchRetry(`${url}/share/${shareId}/view`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    const result = await resp.json();

    return result;
  };

  const findUser = async (query = '', compact = false) => {
    const response = await tryFetchRetry<{ results: User[] }>(
      `${url}/users/find/?query=${query}&compact=${compact.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        retryOn: retryOnStatusCode([503, 504]),
      },
      false
    );
    return response;
  };

  const getIndexedUsers = async (take: number, skip: number) => {
    const endpoint = new URL('users/indexed', url);
    endpoint.searchParams.append('take', take.toString());
    endpoint.searchParams.append('skip', skip.toString());
    const resp = await fetchRetry(endpoint.toString(), { method: 'GET' });
    return resp.json();
  };

  const answerQuestionnaire = async ({
    questionKey,
    answerText,
  }: {
    questionKey: string;
    answerText: string;
  }) => {
    const resp = await fetchRetry(`${url}/users/answer_questionnaire`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ questionKey, answerText }),
    });

    return await resp.json();
  };

  const createReport = async (
    type: string,
    category: string,
    referenceId: string,
    message: string
  ) => {
    const resp = await fetchRetry(`${url}/reports/create/${type}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ category, referenceId, message }),
    });
    const result = await resp.json();

    return result;
  };

  const searchDesigns = async ({
    take,
    skip,
    trash,
    query,
    sort,
    folderId,
  }: GenericSearchArgs<{ folderId?: string }>) => {
    const resp = await fetchRetry(
      `${url}/designs/search?take=${take}&skip=${skip}&sort=${sort}&query=${query}${
        trash ? `&trash=${trash}` : ''
      }${folderId ? `&folderId=${folderId}` : '&folderId=root'}`,
      {
        method: 'GET',
        headers: {
          [HeaderNames.AUTHORIZATION]: `Bearer ${getAccessToken()}`,
          [KittlHeaderNames.WORKSPACE_ID]:
            useWorkspaceStore.getState().activeWorkspaceId ?? '',
        },
      }
    );

    const result = await resp.json();
    return result;
  };

  const searchFolders = async ({
    take,
    skip,
    trash,
    query,
    sort,
  }: GenericSearchArgs) => {
    const resp = await fetchRetry(
      `${url}/folders/search?take=${take}&skip=${skip}&sort=${sort}&query=${query}${
        trash ? `&trash=${trash}` : ''
      }`,
      {
        method: 'GET',
        headers: {
          [HeaderNames.AUTHORIZATION]: `Bearer ${getAccessToken()}`,
          [KittlHeaderNames.WORKSPACE_ID]:
            useWorkspaceStore.getState().activeWorkspaceId ?? '',
        },
      }
    );

    const result = await resp.json();
    return result;
  };

  const createPublicSignedUrl = async ({
    count,
    powChallenge,
  }: {
    count: number;
    powChallenge: PoWChallenge;
  }) => {
    return await tryFetchRetry<
      GenericResponse<{
        results: [{ url: string; objectId: string }];
      }>
    >(`${USER_API_URL}/users/public/create_signed_url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-challenge-nonce': powChallenge?.nonce ?? '',
        'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
      },
      body: JSON.stringify({
        count,
      }),
    });
  };

  return {
    resetPassword,
    update,
    confirmEmail,
    requestConfirmationCode,
    getShare,
    sendFeedback,
    changePassword,
    getInvites,
    createInvite,
    getPublicUser,
    getPromoCode,
    follow,
    unfollow,
    viewShare,
    findUser,
    getIndexedUsers,
    answerQuestionnaire,

    //report
    createReport,

    searchDesigns,
    searchFolders,

    createPublicSignedUrl,
  };
};

export default createAPI({ url: USER_API_URL });
